import React, { useEffect, useState, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import _ from "lodash";
import { customerListActions } from "../../actions";
import useSession from "../../hooks/Auth/useSession";
import useSubmitNewUserRequest from "../../hooks/Communication/useSubmitNewUserRequest";
import useGetCustomer from "../../hooks/CustomerProfile/useGetCustomer";
import { config } from "../../utils/conf";
import "./style.css";

const RequestUserPopup = (props) => {
  const dispatch = useDispatch();
  const modal = useRef();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setemail] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const { accessToken, customerId } = useSession();
  const { submitNewUserRequest, submitNewUserRequestStatus } =
    useSubmitNewUserRequest();
  const { data: customer } = useGetCustomer(customerId);

  const customersRaw = useSelector((state) => state.customers?.customers);
  const customersFetching = useSelector(
    (state) => state.customers?.fetching ?? false,
  );
  const customerOptions = useMemo(
    () => _.orderBy(customersRaw || [], "name", "asc"),
    [customersRaw],
  );

  useEffect(() => {
    if (!accessToken) return;
    dispatch(
      customerListActions.fetchCustomers(
        accessToken,
        config.singleSign ? undefined : customer?.customerType,
      ),
    );
  }, [accessToken, customer?.customerType, dispatch]);

  const customerDefaultAppliedRef = useRef(false);
  useEffect(() => {
    if (
      customerDefaultAppliedRef.current ||
      !customerOptions.length ||
      !customerId
    ) {
      return;
    }
    const match = customerOptions.find(
      (c) => String(c.id) === String(customerId),
    );
    if (match) {
      setSelectedCustomer(match);
      customerDefaultAppliedRef.current = true;
    }
  }, [customerOptions, customerId]);

  const closePopup = () => {
    clear();
    props.closeModal();
  };

  const clear = () => {
    setFirstName("");
    setLastName("");
    setemail("");
    setSelectedCustomer(null);
  };

  const onRequestNewUserSubmit = async (e) => {
    e.preventDefault();
    if (!selectedCustomer) return;

    const targetCustomerId = String(selectedCustomer.id);
    const newUserRequestBody = {
      newUser: {
        firstName,
        lastName,
        email,
        customerId: targetCustomerId,
      },
    };

    props.onSubmittingChange?.(true);

    try {
      await submitNewUserRequest(newUserRequestBody);
      clear();
      props.onSuccess?.();
      props.closeModal();
    } catch (error) {
      props.onError?.(error);
    } finally {
      props.onSubmittingChange?.(false);
    }
  };

  useEffect(() => {
    return () => {
      clear();
    };
  }, []); // eslint-disable-line

  return (
    <div
      className="info-overlay"
      style={{
        position: "fixed",
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 5,
        display: props.visible ? "" : "none",
      }}>
      <div id="request-user" ref={modal}>
        {/* model Header */}
        <div id="request-user-top">
          <div id="request-user-header">
            <h2>{`Request a New User`}</h2>
            <div
              id="cu-close-button"
              style={{ position: "absolute !important" }}>
              <span
                className="glyphicon glyphicon-remove"
                onClick={closePopup}
              />
            </div>
          </div>
        </div>

        <div className="request-user-bottom">
          <form onSubmit={onRequestNewUserSubmit}>
            {/* model body */}
            <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-x-12">
              <div className="tw-col-auto  field-sep">
                <label htmlFor="firstName" className="field-title">
                  First Name
                </label>
                <input
                  type="text"
                  id="firstName"
                  className="form-control field-text"
                  value={firstName}
                  onChange={(e) => {
                    setFirstName(e.target.value);
                  }}
                  required
                />
              </div>

              <div className="tw-col-auto field-sep">
                <label htmlFor="lastName" className="field-title">
                  Last Name
                </label>
                <input
                  type="text"
                  id="lastName"
                  className="form-control field-text"
                  value={lastName}
                  onChange={(e) => {
                    setLastName(e.target.value);
                  }}
                  required
                />
              </div>

              <div className="tw-col-auto field-sep">
                <label htmlFor="email" className="field-title">
                  Email address
                </label>
                <input
                  type="email"
                  id="email"
                  className="form-control field-text"
                  value={email}
                  onChange={(e) => {
                    setemail(e.target.value);
                  }}
                  required
                />
              </div>

              <div className="tw-col-auto field-sep tw-hidden sm:tw-block"></div>

              <div className="tw-col-auto field-sep">
                <label htmlFor="requestUserCustomer" className="field-title">
                  Default Organization
                </label>
                <Select
                  inputId="requestUserCustomer"
                  name="requestUserCustomer"
                  className="react-select tw-font-museo"
                  classNamePrefix="react-select"
                  menuPortalTarget={
                    typeof document !== "undefined" ? document.body : null
                  }
                  menuPosition="fixed"
                  options={customerOptions}
                  value={selectedCustomer}
                  isLoading={customersFetching}
                  isDisabled={customersFetching}
                  onChange={(option) => setSelectedCustomer(option)}
                  getOptionLabel={(option) => option.name}
                  getOptionValue={(option) => option.id}
                  placeholder="Select a customer"
                  isClearable={false}
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    menuList: (base) => ({
                      ...base,
                      maxHeight: 240,
                    }),
                    option: (base, state) => ({
                      ...base,
                      cursor: state.isDisabled ? "default" : "pointer",
                    }),
                  }}
                  theme={(theme) => ({
                    ...theme,
                    borderRadius: "4px",
                    colors: {
                      ...theme.colors,
                      primary25: "#E0E0E0",
                      primary: "#E0E0E0",
                    },
                  })}
                />
              </div>

              <div className="tw-col-auto field-sep tw-hidden sm:tw-block"></div>
            </div>

            {/* model footer */}
            <div className="cu-footer">
              <button
                type="button"
                className="btn btn-secondary modal-footer-cancel-btn"
                data-dismiss="modal"
                onClick={closePopup}>
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary modal-footer-submin-btn"
                disabled={
                  !selectedCustomer ||
                  customersFetching ||
                  submitNewUserRequestStatus.isLoading
                }>
                Submit
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RequestUserPopup;
